import app from 'flarum/forum/app';

/**
 * Long-form reading aids (the UX-feedback round): a slim reading-progress bar, a
 * floating back-to-top button, and an auto Table of Contents built from the
 * headings in a discussion's opening post / article. All plain-DOM + a debounced
 * MutationObserver so it tracks Flarum's SPA navigation with no per-component
 * hooks, and stays cheap (one throttled scroll handler).
 *
 * Reading aids only appear where they help — a discussion/content page with a
 * real post stream — so the index stays clean.
 */

let inited = false;
let rafPending = false;
function tr(key) {
  const r = app.translator.trans('ernestdefoe-bespoke.forum.' + key);
  return Array.isArray(r) ? r.join('') : String(r);
}
export function initReadability() {
  if (inited || typeof document === 'undefined') return;
  inited = true;
  buildChrome();
  window.addEventListener('scroll', onScroll, {
    passive: true
  });
  window.addEventListener('resize', onScroll, {
    passive: true
  });

  // Rebuild the TOC whenever the page content changes (SPA nav), debounced.
  let deb;
  try {
    const obs = new MutationObserver(() => {
      clearTimeout(deb);
      deb = setTimeout(refresh, 250);
    });
    obs.observe(document.body, {
      childList: true,
      subtree: true
    });
  } catch (e) {
    /* no MutationObserver → still works on first paint + scroll */
  }
  refresh();
}
function buildChrome() {
  if (document.getElementById('bespoke-progress')) return;
  const bar = document.createElement('div');
  bar.id = 'bespoke-progress';
  bar.className = 'Bespoke-progress';
  document.body.appendChild(bar);
  const top = document.createElement('button');
  top.id = 'bespoke-totop';
  top.type = 'button';
  top.className = 'Bespoke-toTop';
  top.setAttribute('aria-label', tr('to_top'));
  top.innerHTML = '<i class="fas fa-arrow-up"></i>';
  top.addEventListener('click', () => window.scrollTo({
    top: 0,
    behavior: 'smooth'
  }));
  document.body.appendChild(top);
  const toc = document.createElement('div');
  toc.id = 'bespoke-toc';
  toc.className = 'Bespoke-toc';
  toc.innerHTML = '<button type="button" class="Bespoke-toc-toggle" aria-label="' + tr('toc') + '"><i class="fas fa-list-ul"></i></button>' + '<nav class="Bespoke-toc-panel"><div class="Bespoke-toc-head">' + tr('toc') + '</div><div class="Bespoke-toc-list"></div></nav>';
  document.body.appendChild(toc);
  toc.querySelector('.Bespoke-toc-toggle').addEventListener('click', () => toc.classList.toggle('is-open'));
}
function onScroll() {
  if (rafPending) return;
  rafPending = true;
  requestAnimationFrame(() => {
    rafPending = false;
    const h = document.documentElement;
    const max = h.scrollHeight - h.clientHeight;
    const pct = max > 0 ? h.scrollTop / max : 0;

    // Show the reading-progress bar on any meaningfully scrollable page (not just
    // discussions) so it's actually discoverable.
    const bar = document.getElementById('bespoke-progress');
    if (bar) {
      bar.style.width = max > 200 ? pct * 100 + '%' : '0';
    }
    const showToTop = h.scrollTop > 300;
    const top = document.getElementById('bespoke-totop');
    if (top) top.classList.toggle('is-visible', showToTop);
    // Mirror the to-top visibility onto the body so CSS can lift the editor
    // launcher clear of the (bottom-right) back-to-top button while it shows.
    document.body.classList.toggle('bespoke-totop-visible', showToTop);
    highlightToc();
  });
}

/** The opening post's body (where blog-style / long content lives). */
function opBody() {
  return document.querySelector('.PostStream-item .Post-body') || document.querySelector('.PostStream .Post-body') || document.querySelector('.Post-body');
}
function refresh() {
  const toc = document.getElementById('bespoke-toc');
  const list = toc && toc.querySelector('.Bespoke-toc-list');
  const body = opBody();
  if (!toc || !list) return;
  const heads = body ? Array.from(body.querySelectorAll('h1, h2, h3')).filter(h => (h.textContent || '').trim()) : [];

  // Only worth a TOC when there are a few sections.
  if (heads.length < 2) {
    toc.classList.remove('is-available', 'is-open');
    list.innerHTML = '';
    return;
  }
  list.innerHTML = '';
  heads.forEach((h, i) => {
    if (!h.id) h.id = 'bespoke-h-' + i;
    const a = document.createElement('a');
    a.className = 'Bespoke-toc-link Bespoke-toc-' + h.tagName.toLowerCase();
    a.textContent = (h.textContent || '').trim();
    a.href = '#' + h.id;
    a.addEventListener('click', e => {
      e.preventDefault();
      h.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
      toc.classList.remove('is-open');
    });
    list.appendChild(a);
  });
  toc.classList.add('is-available');
  onScroll();
}

/** Mark the TOC link for the section currently near the top of the viewport. */
function highlightToc() {
  const toc = document.getElementById('bespoke-toc');
  if (!toc || !toc.classList.contains('is-available')) return;
  const links = Array.from(toc.querySelectorAll('.Bespoke-toc-link'));
  let activeId = '';
  for (const a of links) {
    const id = (a.getAttribute('href') || '').slice(1);
    const target = id && document.getElementById(id);
    if (target && target.getBoundingClientRect().top <= 120) activeId = id;
  }
  links.forEach(a => a.classList.toggle('is-active', (a.getAttribute('href') || '').slice(1) === activeId));
}